import { useCallback, useEffect, useMemo } from 'react';
import { getIn, useFormikContext } from 'formik';

import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';

import { EMPTY_STRING } from '@e-socna/empty';
import type { ProjectionId } from '@e-socna/data-layer';

import { getTouchedError } from '../../../../utils/get-touched-error.util';
import { useReferencesContext } from '../../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useFormikOnNextTick } from '../../../../hooks/useFormikOnNextTick.hook';
import { IdSelect } from '../../../../components/IdSelect';
import type { IdOption } from '../../../../components/IdSelect';

import type { AddressForm } from '../../types/address-form.type';

interface Props {
  readonly id: string;
}

// eslint-disable-next-line no-empty-pattern
// A number input accepts integers only by default (step 1): the coordinates
// and the altitude are decimals.
const ANY_DECIMAL = { htmlInput: { step: 'any' } };

export const AddressLocationInfoFormStep = ({}: Readonly<Props>) => {
  const { errors, handleBlur, handleChange, touched, values } =
    useFormikContext<AddressForm>();

  const { setFieldValue: setFieldValueOnNextTick } = useFormikOnNextTick();

  const { projections, defaultProjection } = useReferencesContext();

  useEffect(() => {
    setFieldValueOnNextTick(
      'locationInfo.projectionId',
      defaultProjection?._id ?? EMPTY_STRING,
    );
  }, [defaultProjection?._id, setFieldValueOnNextTick]);

  const projectionOptions = useMemo<readonly IdOption<ProjectionId>[]>(
    () =>
      projections.map((projection) => ({
        id: projection._id,
        label: projection.label,
      })),
    [projections],
  );

  const handleProjectionChange = useCallback(
    (id: ProjectionId | typeof EMPTY_STRING) => {
      setFieldValueOnNextTick('locationInfo.projectionId', id);
    },
    [setFieldValueOnNextTick],
  );

  return (
    <Grid container spacing={2} sx={{ alignContent: 'flex-start' }}>
      <Grid size={12} sx={{ pt: 1, pb: 1 }}>
        <IdSelect
          disabled
          error={
            !!(
              getIn(errors, 'locationInfo.projectionId') &&
              getIn(touched, 'locationInfo.projectionId')
            )
          }
          helperText={
            !!(
              getIn(errors, 'locationInfo.projectionId') &&
              getIn(touched, 'locationInfo.projectionId')
            ) && (getIn(errors, 'locationInfo.projectionId') as string)
          }
          id="address-location-info-projection-id"
          label="Projection"
          name="locationInfo.projectionId"
          onBlur={handleBlur}
          onChange={handleProjectionChange}
          options={projectionOptions}
          required
          value={
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
            (values.locationInfo?.projectionId as ProjectionId | undefined) ||
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
            defaultProjection?._id ||
            EMPTY_STRING
          }
        />
      </Grid>
      <Grid
        size={{
          sm: 6,
          xs: 12,
        }}
        sx={{ pt: 1, pb: 1 }}
      >
        <TextField
          error={
            !!(
              getIn(errors, 'locationInfo.coordinates[1]') &&
              getIn(touched, 'locationInfo.coordinates[1]')
            )
          }
          fullWidth
          helperText={getTouchedError(
            errors,
            touched,
            'locationInfo.coordinates[1]',
          )}
          id="address-lcoation-info-coordinates-1"
          label="Latitude"
          name="locationInfo.coordinates[1]"
          onBlur={handleBlur}
          onChange={handleChange}
          required
          slotProps={ANY_DECIMAL}
          type="number"
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          value={values.locationInfo?.coordinates?.[1] ?? EMPTY_STRING}
          variant="outlined"
        />
      </Grid>
      <Grid
        size={{
          sm: 6,
          xs: 12,
        }}
        sx={{ pt: 1, pb: 1 }}
      >
        <TextField
          error={
            !!(
              getIn(errors, 'locationInfo.coordinates[0]') &&
              getIn(touched, 'locationInfo.coordinates[0]')
            )
          }
          fullWidth
          helperText={getTouchedError(
            errors,
            touched,
            'locationInfo.coordinates[0]',
          )}
          id="address-lcoation-info-coordinates-0"
          label="Longitude"
          name="locationInfo.coordinates[0]"
          onBlur={handleBlur}
          onChange={handleChange}
          required
          slotProps={ANY_DECIMAL}
          type="number"
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          value={values.locationInfo?.coordinates?.[0] ?? EMPTY_STRING}
          variant="outlined"
        />
      </Grid>
      <Grid size={12} sx={{ pt: 1, pb: 1 }}>
        <TextField
          error={
            !!(
              getIn(errors, 'locationInfo.altitude') &&
              getIn(touched, 'locationInfo.altitude')
            )
          }
          fullWidth
          helperText={getTouchedError(errors, touched, 'locationInfo.altitude')}
          id="address-lcoation-info-altitude"
          label="Altitude en mètres"
          name="locationInfo.altitude"
          onBlur={handleBlur}
          onChange={handleChange}
          required
          slotProps={ANY_DECIMAL}
          type="number"
          value={values.locationInfo?.altitude ?? EMPTY_STRING}
          variant="outlined"
        />
      </Grid>
    </Grid>
  );
};
